import { TEMPLATE_PREVIEW_CAP_PX } from "./templatePreviewCap";

export type TemplatePreviewDevice = keyof typeof TEMPLATE_PREVIEW_CAP_PX;

/** Shared layout breakpoints — keep in sync with FollowerView mobile (< 890). */
export const TEMPLATE_LAYOUT_MOBILE_MAX_PX = 890;
export const TEMPLATE_LAYOUT_TABLET_MAX_PX = 1024;

/**
 * Layout / nav breakpoints (FollowerView mobile < 890px).
 * Public `/website` uses the measured page width with these tiers.
 */
export function getDeviceForTemplateLayoutWidth(
  w: number,
): TemplatePreviewDevice {
  if (!Number.isFinite(w) || w <= 0) return "desktop";
  if (w < TEMPLATE_LAYOUT_MOBILE_MAX_PX) return "mobile";
  if (w < TEMPLATE_LAYOUT_TABLET_MAX_PX) return "tablet";
  return "desktop";
}

/**
 * Same tiers as `getDeviceForTemplateLayoutWidth`, with hysteresis so continuous
 * resize does not flip mobile ↔ tablet ↔ desktop on every pixel near a boundary.
 */
export function getDeviceForTemplateLayoutWidthStable(
  w: number,
  prev: TemplatePreviewDevice,
  pad = 24,
): TemplatePreviewDevice {
  if (!Number.isFinite(w) || w <= 0) return prev || "desktop";
  const mobileExit = TEMPLATE_LAYOUT_MOBILE_MAX_PX + pad;
  const tabletExit = TEMPLATE_LAYOUT_TABLET_MAX_PX + pad;
  if (prev === "mobile") {
    if (w < mobileExit) return "mobile";
    if (w < TEMPLATE_LAYOUT_TABLET_MAX_PX) return "tablet";
    return "desktop";
  }
  if (prev === "tablet") {
    if (w < TEMPLATE_LAYOUT_MOBILE_MAX_PX) return "mobile";
    if (w < tabletExit) return "tablet";
    return "desktop";
  }
  // desktop
  if (w < TEMPLATE_LAYOUT_MOBILE_MAX_PX) return "mobile";
  if (w < TEMPLATE_LAYOUT_TABLET_MAX_PX) return "tablet";
  return "desktop";
}

/** True when layout width is in the mobile tier (prefer over window.innerWidth). */
export function isTemplateMobileWidth(w: number): boolean {
  return getDeviceForTemplateLayoutWidth(w) === "mobile";
}
